import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  CreditCard,
  Users,
  Briefcase,
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  Loader2,
  ArrowLeft,
  Star,
  Zap,
  ShoppingCart,
  XCircle,
  Info,
  ArrowRight,
  RotateCcw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import {
  getBillingState,
  BillingState,
  BillingSubscription,
} from '../../lib/billingState';

// ── Local types ────────────────────────────────────────────────────────────

interface TeamMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  has_pro_job_access: boolean;
  in_grace_period: boolean;
  pro_grace_until: string | null;
  plan: string;
}

interface JobCredits {
  credits_remaining: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Component ──────────────────────────────────────────────────────────────

export default function Billing() {
  const navigate = useNavigate();
  const { user, session } = useAuth();

  const [sub, setSub]                     = useState<BillingSubscription | null>(null);
  const [teamMembers, setTeamMembers]     = useState<TeamMember[]>([]);
  const [credits, setCredits]             = useState<JobCredits | null>(null);
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast]                 = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [pendingCheckout, setPendingCheckout]     = useState(false);
  const [activationStuck,  setActivationStuck]    = useState(false);
  const activationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Single billing brain — every UI decision derives from this
  const billingState: BillingState = getBillingState(sub);
  const totalSeats = (sub?.seats_included ?? 0) + (sub?.seats_extra ?? 0);

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [subRes, membersRes, creditsRes, profileRes] = await Promise.all([
        supabase
          .from('billing_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('workday_client_profiles')
          .select('id, email, first_name, last_name, role, has_pro_job_access, in_grace_period, pro_grace_until, plan')
          .eq('invited_by', user.id),
        supabase
          .from('job_credits')
          .select('credits_remaining')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('workday_client_profiles')
          .select('pending_checkout')
          .eq('id', user.id)
          .maybeSingle(),
      ]);

      setSub(subRes.data ?? null);
      setTeamMembers(membersRes.data ?? []);
      setCredits(creditsRes.data ?? { credits_remaining: 0 });
      // pending_checkout shows a resume banner; cleared only by webhook, never by UI writes
      if (profileRes.data) {
        setPendingCheckout(!!profileRes.data.pending_checkout);
      }
    } catch (err) {
      console.error('Billing load error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // If stuck in PENDING_ACTIVATION for > 5 min, surface a support prompt
  useEffect(() => {
    if (billingState === 'PENDING_ACTIVATION') {
      activationTimer.current = setTimeout(() => setActivationStuck(true), 5 * 60 * 1000);
    } else {
      setActivationStuck(false);
      if (activationTimer.current) clearTimeout(activationTimer.current);
    }
    return () => { if (activationTimer.current) clearTimeout(activationTimer.current); };
  }, [billingState]);

  // ── Cancel subscription ───────────────────────────────────────────────────
  const handleCancel = async () => {
    setShowCancelConfirm(false);
    setActionLoading('cancel');
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paypal-cancel-subscription`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: 'Cancelled by account owner' }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Cancel failed');
      showToast('success', `Subscription cancelled. Pro access continues until ${formatDate(data.plan_expires_at)}.`);
      await loadData();
    } catch (err: unknown) {
      showToast('error', (err as Error).message ?? 'Cancel failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Add seat via PayPal ───────────────────────────────────────────────────
  const handleAddSeat = async () => {
    setActionLoading('seat');
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paypal-add-seat`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Order creation failed');
      window.open(
        `https://www.paypal.com/checkoutnow?token=${data.order_id}`,
        '_blank',
        'width=600,height=700'
      );
      showToast('success', 'PayPal checkout opened. Seat will be added after payment.');
    } catch (err: unknown) {
      showToast('error', (err as Error).message ?? 'Failed to start seat purchase.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Buy job credits ───────────────────────────────────────────────────────
  const handleBuyCredits = async (quantity: 15 | 35 | 65) => {
    setActionLoading(`credits_${quantity}`);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paypal-buy-job-credits`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ quantity }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Order creation failed');
      window.open(
        `https://www.paypal.com/checkoutnow?token=${data.order_id}`,
        '_blank',
        'width=600,height=700'
      );
      showToast('success', `PayPal checkout opened. ${quantity} credits will be added after payment.`);
    } catch (err: unknown) {
      showToast('error', (err as Error).message ?? 'Failed to start credits purchase.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Pro Job Access toggle ─────────────────────────────────────────────────
  const handleProJobToggle = async (memberId: string, current: boolean, inGrace: boolean) => {
    if (inGrace) return;
    setActionLoading(`toggle_${memberId}`);
    try {
      const { error } = await supabase
        .from('workday_client_profiles')
        .update({ has_pro_job_access: !current })
        .eq('id', memberId);
      if (error) throw error;
      setTeamMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, has_pro_job_access: !current } : m))
      );
    } catch {
      showToast('error', 'Failed to update seat access.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Plan badge ────────────────────────────────────────────────────────────
  const PlanBadge = () => {
    switch (billingState) {
      case 'PRO_ACTIVE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            <CheckCircle className="w-3 h-3" /> Active
          </span>
        );
      case 'PRO_GRACE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
            <Clock className="w-3 h-3" /> Grace Period
          </span>
        );
      case 'PENDING_ACTIVATION':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
            <Loader2 className="w-3 h-3 animate-spin" /> Activating…
          </span>
        );
      case 'CANCELLED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">
            <XCircle className="w-3 h-3" /> Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-gray-400 border border-white/10">
            <XCircle className="w-3 h-3" /> Free
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const creditsRemaining  = credits?.credits_remaining ?? 0;
  const canPurchase       = billingState === 'PRO_ACTIVE';
  const hasProOrGrace     = billingState === 'PRO_ACTIVE' || billingState === 'PRO_GRACE';

  const planLabel = {
    PRO_ACTIVE:          `Pro — ${sub?.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} billing`,
    PRO_GRACE:           'Pro (grace period active)',
    PENDING_ACTIVATION:  'Pro — activating…',
    CANCELLED:           'Cancelled — subscription ended',
    FREE:                'Free — Perplexity-managed hiring',
  }[billingState];

  const planDisplay = {
    PRO_ACTIVE:          'Pro',
    PRO_GRACE:           'Pro (Grace)',
    PENDING_ACTIVATION:  'Activating…',
    CANCELLED:           'Cancelled',
    FREE:                'Free',
  }[billingState];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <WorkdayClientHeader />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 flex items-start gap-3 px-5 py-4 rounded-xl shadow-2xl border max-w-sm text-sm font-medium transition-all ${
            toast.type === 'success'
              ? 'bg-emerald-900/90 border-emerald-500/40 text-emerald-200'
              : 'bg-red-900/90 border-red-500/40 text-red-200'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-400" />
          ) : (
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-400" />
          )}
          {toast.msg}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 pt-28 pb-20">
        {/* Back */}
        <button
          onClick={() => navigate('/workday-client/settings')}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Settings
        </button>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-white mb-2">Subscription & Billing</h1>
          <p className="text-gray-400 text-sm">Manage your plan, seats, and job credits.</p>
        </div>

        {/* PENDING_ACTIVATION banner */}
        {billingState === 'PENDING_ACTIVATION' && (
          <div className={`flex items-start gap-3 border rounded-xl px-5 py-4 mb-6 ${
            activationStuck
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-cyan-500/10 border-cyan-500/30'
          }`}>
            {activationStuck
              ? <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              : <Loader2 className="w-5 h-5 text-cyan-400 flex-shrink-0 animate-spin mt-0.5" />
            }
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${activationStuck ? 'text-amber-300' : 'text-cyan-300'}`}>
                {activationStuck ? 'Activation is taking longer than expected' : 'Activating your Pro account…'}
              </p>
              <p className={`text-xs mt-0.5 ${activationStuck ? 'text-amber-400/80' : 'text-cyan-400/80'}`}>
                {activationStuck
                  ? 'Your payment was received but the subscription webhook has not confirmed yet. Try refreshing, or contact support if this persists.'
                  : 'Payment received — waiting for subscription confirmation. This usually takes under a minute.'}
              </p>
              {activationStuck && (
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={loadData}
                    className="flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Refresh status
                  </button>
                  <a
                    href="mailto:support@perplexityats.com"
                    className="text-xs text-amber-400/70 hover:text-amber-300 transition-colors underline"
                  >
                    Contact support
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pending checkout banner — resume incomplete checkout */}
        {pendingCheckout && billingState === 'FREE' && (
          <div className="flex items-center justify-between gap-4 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 mb-6">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-300">Finish activating Pro</p>
                <p className="text-xs text-amber-400/80 mt-0.5">Your account is on the Free plan until you complete checkout.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/workday-client/checkout')}
              className="flex-shrink-0 flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold px-4 py-2 rounded-lg transition-colors"
            >
              Complete Checkout
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* PRO_GRACE banner */}
        {billingState === 'PRO_GRACE' && sub?.plan_expires_at && (
          <div className="mb-8 flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 font-semibold text-sm">Pro access active during grace period</p>
              <p className="text-amber-400/80 text-xs mt-0.5">
                Full Pro features remain available until{' '}
                <span className="font-bold">{formatDate(sub.plan_expires_at)}</span>.
                After that, your account reverts to Free.
              </p>
            </div>
          </div>
        )}

        {/* ── Section 1: Plan Card ─────────────────────────────────────── */}
        <section className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/20">
                <CreditCard className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Current Plan</h2>
                <p className="text-gray-400 text-xs mt-0.5">{planLabel}</p>
              </div>
            </div>
            <PlanBadge />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-black/30 rounded-xl p-4 border border-white/5">
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Plan</p>
              <p className="text-white font-bold text-lg">{planDisplay}</p>
            </div>
            <div className="bg-black/30 rounded-xl p-4 border border-white/5">
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">
                {billingState === 'PRO_GRACE' ? 'Access Expires' : billingState === 'PRO_ACTIVE' ? 'Renews' : 'Billing'}
              </p>
              <p className="text-white font-bold text-lg">
                {billingState === 'FREE' || billingState === 'CANCELLED' ? '$0 / mo' : formatDate(sub?.plan_expires_at ?? null)}
              </p>
            </div>
            <div className="bg-black/30 rounded-xl p-4 border border-white/5">
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Total Seats</p>
              <p className="text-white font-bold text-lg">
                {!hasProOrGrace ? 'Unlimited' : `${totalSeats} (${sub?.seats_included ?? 3} base + ${sub?.seats_extra ?? 0} extra)`}
              </p>
            </div>
          </div>

          {/* Upgrade CTA — FREE */}
          {billingState === 'FREE' && (
            <div>
              <div className="flex items-start gap-2 bg-cyan-500/5 border border-cyan-500/15 rounded-lg px-4 py-3 mb-4">
                <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-cyan-300/80 text-xs">
                  Upgrade to Pro to unlock self-service job posting, direct talent access, team seats, and candidate pipeline tracking.
                </p>
              </div>
              <button
                onClick={() => navigate('/workday-client/upgrade')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-sm font-bold hover:from-cyan-400 hover:to-cyan-500 transition-all shadow-lg shadow-cyan-500/20 group"
              >
                <Zap className="w-4 h-4" />
                Upgrade to Pro
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          )}

          {/* Re-subscribe CTA — CANCELLED (previously had Pro) */}
          {billingState === 'CANCELLED' && (
            <div>
              <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded-lg px-4 py-3 mb-4">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 text-xs font-semibold mb-0.5">Your Pro subscription has ended</p>
                  <p className="text-amber-400/70 text-xs">
                    Re-subscribe to restore self-service hiring, candidate pipeline, team seats, and job credit packs. Your previous settings and data are still here.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/workday-client/upgrade')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-sm font-bold hover:from-cyan-400 hover:to-cyan-500 transition-all shadow-lg shadow-cyan-500/20 group"
              >
                <Zap className="w-4 h-4" />
                Re-subscribe to Pro
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          )}

          {/* Cancel — PRO_ACTIVE only */}
          {billingState === 'PRO_ACTIVE' && (
            <div className="flex items-center gap-3">
              {!showCancelConfirm ? (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={!!actionLoading}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all disabled:opacity-40"
                >
                  Cancel Subscription
                </button>
              ) : (
                <div className="flex items-center gap-3 bg-red-900/20 border border-red-500/30 rounded-xl px-4 py-3">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <p className="text-red-300 text-sm">
                    Cancel and start grace period until end of billing cycle?
                  </p>
                  <button
                    onClick={handleCancel}
                    disabled={actionLoading === 'cancel'}
                    className="px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-semibold hover:bg-red-500/30 transition-all disabled:opacity-40 flex items-center gap-1"
                  >
                    {actionLoading === 'cancel' && <Loader2 className="w-3 h-3 animate-spin" />}
                    Confirm Cancel
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="text-gray-400 hover:text-white text-xs transition-colors"
                  >
                    Keep Plan
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Re-subscribe reminder — PRO_GRACE */}
          {billingState === 'PRO_GRACE' && sub?.plan_expires_at && (
            <p className="text-amber-400/70 text-xs">
              Pro features active until <span className="font-semibold text-amber-300">{formatDate(sub.plan_expires_at)}</span>.
              Upgrade again to restore full Pro access.
            </p>
          )}
        </section>

        {/* ── Section 2: Seat Management ────────────────────────────────── */}
        {hasProOrGrace && (
          <section className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/20">
                  <Users className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Seat Management</h2>
                  <p className="text-gray-400 text-xs mt-0.5">
                    {totalSeats} total seats ({sub?.seats_included ?? 3} included + {sub?.seats_extra ?? 0} purchased)
                  </p>
                </div>
              </div>
              {/* Add Seat: PRO_ACTIVE only — grace users cannot purchase new seats */}
              {canPurchase ? (
                <button
                  onClick={handleAddSeat}
                  disabled={!!actionLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-sm font-medium hover:bg-cyan-500/25 transition-all disabled:opacity-40"
                >
                  {actionLoading === 'seat' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Add Seat — $99.99
                </button>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  Renew Pro to add seats
                </div>
              )}
            </div>

            {teamMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                No team members invited yet.{' '}
                <button
                  onClick={() => navigate('/workday-client/invite-team')}
                  className="text-cyan-400 hover:underline"
                >
                  Invite your team
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_120px_140px] gap-3 px-3 pb-2 border-b border-white/5">
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Member</p>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Role</p>
                  <p className="text-gray-500 text-xs uppercase tracking-wide text-center">Pro Job Access</p>
                </div>

                {teamMembers.map((member) => {
                  // Toggle only allowed on PRO_ACTIVE or PRO_GRACE when member not already in individual grace
                  const canToggle   = hasProOrGrace && !member.in_grace_period;
                  const isToggling  = actionLoading === `toggle_${member.id}`;
                  return (
                    <div
                      key={member.id}
                      className="grid grid-cols-[1fr_120px_140px] gap-3 items-center px-3 py-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-gray-500 text-xs truncate">{member.email}</p>
                      </div>
                      <p className="text-gray-300 text-xs capitalize">{member.role.replace('_', ' ')}</p>
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => handleProJobToggle(member.id, member.has_pro_job_access, member.in_grace_period)}
                          disabled={!canToggle || isToggling}
                          className={`relative w-10 h-5 rounded-full transition-all focus:outline-none ${
                            member.has_pro_job_access ? 'bg-cyan-500' : 'bg-white/10'
                          } ${!canToggle ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                          title={member.in_grace_period ? `Until ${formatDate(member.pro_grace_until)}` : ''}
                        >
                          {isToggling ? (
                            <Loader2 className="absolute inset-0 m-auto w-3 h-3 animate-spin text-white" />
                          ) : (
                            <span
                              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                member.has_pro_job_access ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          )}
                        </button>
                        {member.in_grace_period && member.pro_grace_until && (
                          <span className="text-amber-400/70 text-[10px] font-medium">
                            Until {formatDate(member.pro_grace_until)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ── Section 3: Job Credit Packs ───────────────────────────────── */}
        <section className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/20">
                <Briefcase className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Job Credit Packs</h2>
                <p className="text-gray-400 text-xs mt-0.5">
                  Credits remaining:{' '}
                  <span className={`font-bold ${creditsRemaining > 0 ? 'text-cyan-300' : 'text-gray-400'}`}>
                    {creditsRemaining}
                  </span>
                </p>
              </div>
            </div>
            {!canPurchase && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-lg">
                <Star className="w-3.5 h-3.5 flex-shrink-0" />
                Pro only
              </span>
            )}
          </div>

          {canPurchase ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(
                  [
                    { qty: 15, price: '$199.99', badge: 'Starter',    desc: 'Light hiring needs' },
                    { qty: 35, price: '$399.99', badge: 'Best Value',  desc: 'Multi-role hiring' },
                    { qty: 65, price: '$699.99', badge: 'High Volume', desc: 'Large-scale hiring' },
                  ] as const
                ).map(({ qty, price, badge, desc }) => {
                  const isLoading = actionLoading === `credits_${qty}`;
                  return (
                    <div
                      key={qty}
                      className="bg-black/30 border border-white/5 rounded-xl p-4 flex flex-col gap-3 hover:border-cyan-500/20 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-bold text-base">{qty} Job Posts</p>
                          <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
                        </div>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">
                          {badge}
                        </span>
                      </div>
                      <p className="text-cyan-300 font-bold text-lg">{price}</p>
                      <button
                        onClick={() => handleBuyCredits(qty)}
                        disabled={!!actionLoading}
                        className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-sm font-medium hover:bg-cyan-500/25 transition-all disabled:opacity-40"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
                        Buy via PayPal
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-gray-600 text-xs mt-4">
                Credits are added to your account instantly after PayPal payment is confirmed.
              </p>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-white font-semibold mb-1">
                  {billingState === 'PRO_GRACE'
                    ? 'Renew Pro to purchase job credit packs'
                    : billingState === 'PENDING_ACTIVATION'
                    ? 'Activation in progress — check back shortly'
                    : 'Upgrade to Pro to purchase job credit packs'}
                </p>
                <p className="text-gray-500 text-sm max-w-sm">
                  {billingState === 'PRO_GRACE'
                    ? 'Your Pro subscription is in its grace period. Re-subscribe to unlock credit pack purchases.'
                    : billingState === 'PENDING_ACTIVATION'
                    ? 'Your subscription is still activating. Purchases will be available once activation is confirmed.'
                    : 'Job credit packs are available exclusively to active Pro subscribers.'}
                </p>
              </div>
              {billingState !== 'PENDING_ACTIVATION' && (
                <button
                  onClick={() => navigate('/workday-client/upgrade')}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white text-sm font-bold hover:from-cyan-400 hover:to-cyan-500 transition-all shadow-lg shadow-cyan-500/20"
                >
                  <Zap className="w-4 h-4" />
                  {billingState === 'PRO_GRACE' ? 'Re-subscribe to Pro' : 'Upgrade to Pro'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </section>

        {/* ── Section 4: Pro Features Summary ──────────────────────────── */}
        <section className="bg-white/[0.04] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/20">
              <Zap className="w-5 h-5 text-cyan-400" />
            </div>
            <h2 className="text-lg font-bold text-white">What's Included</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              { label: 'Job Posting',                        free: 'Via Perplexity team', pro: 'Self-service' },
              { label: 'Browse Workday Talent',              free: true,                  pro: true },
              { label: 'Interview Request & Scheduling',     free: 'Via Perplexity',      pro: 'Self-service' },
              { label: 'Hiring & Onboarding',                free: 'Via Perplexity',      pro: 'Via Perplexity' },
              { label: 'Timesheet Approvals',                free: 'Via Perplexity',      pro: 'Via Perplexity' },
              { label: 'Candidate Pipeline & Tracking',      free: false,                 pro: true },
              { label: 'Direct Candidate Messaging',         free: false,                 pro: true },
              { label: 'Pro Job Postings (company revealed)',free: false,                 pro: 'Per-seat toggle' },
              { label: 'Document Management',                free: true,                  pro: true },
              { label: 'Priority Support',                   free: true,                  pro: true },
              { label: 'Team Seats',                         free: 'Unlimited',           pro: '3 included + $99.99 each' },
            ].map(({ label, free, pro }) => (
              <div key={label} className="flex items-start gap-3 py-2 border-b border-white/5">
                <div className="min-w-0 flex-1">
                  <p className="text-gray-300 text-sm">{label}</p>
                </div>
                <div className="flex gap-4 flex-shrink-0 text-xs">
                  <div className="w-16 text-center">
                    {free === false ? (
                      <XCircle className="w-4 h-4 text-gray-600 mx-auto" />
                    ) : free === true ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" />
                    ) : (
                      <span className="text-gray-500 leading-tight block">{free}</span>
                    )}
                  </div>
                  <div className="w-20 text-center">
                    {pro === false ? (
                      <XCircle className="w-4 h-4 text-gray-600 mx-auto" />
                    ) : pro === true ? (
                      <CheckCircle className="w-4 h-4 text-cyan-400 mx-auto" />
                    ) : (
                      <span className="text-cyan-400/80 leading-tight block">{pro}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-6 mt-4 pt-3 border-t border-white/5">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-600" />
              <span className="text-gray-400 text-xs">Free</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-cyan-500" />
              <span className="text-gray-400 text-xs">Pro</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
